import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Zap, ArrowLeft, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import WizardProgressSidebar from './components/WizardProgressSidebar';
import Step1Welcome from './components/Step1Welcome';
import Step2CompanyProfile from './components/Step2CompanyProfile';
import Step3FirstProduct from './components/Step3FirstProduct';
import Step4Checklist from './components/Step4Checklist';

export default function PartnerOnboardingWizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [partnerUser, setPartnerUser] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [productSkipped, setProductSkipped] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        if (!session?.user) {
          navigate('/retail-partner-portal', { replace: true });
          return;
        }
        const { data: profile } = await supabase
          ?.from('partner_profiles')
          ?.select('*')
          ?.eq('id', session?.user?.id)
          ?.maybeSingle();
        if (!profile) {
          navigate('/retail-partner-portal', { replace: true });
          return;
        }
        // If already completed, redirect to portal
        if (profile?.onboarding_completed) {
          navigate('/retail-partner-portal', { replace: true });
          return;
        }
        setPartnerUser(session?.user);
        setPartnerProfile(profile);

        // Restore step from URL param or saved step
        const urlStep = parseInt(searchParams?.get('step'));
        const savedStep = profile?.onboarding_step ?? 1;
        const startStep = urlStep || savedStep || 1;
        setCurrentStep(Math.min(Math.max(startStep, 1), 4));

        // Mark previously completed steps
        const done = [];
        if (savedStep > 1) done?.push(1);
        if (savedStep > 2) done?.push(2);
        if (savedStep > 3) done?.push(3);
        setCompletedSteps(done);
      } catch (e) {
        console.error('Onboarding init error:', e);
        navigate('/retail-partner-portal', { replace: true });
      } finally {
        setAuthLoading(false);
      }
    };
    init();
  }, []);

  const handleStepComplete = (stepNumber) => {
    setCompletedSteps(prev => {
      if (!prev?.includes(stepNumber)) return [...prev, stepNumber];
      return prev;
    });
    if (stepNumber < 4) {
      setCurrentStep(stepNumber + 1);
    }
  };

  const handleProductSkip = async () => {
    setProductSkipped(true);
    await supabase?.from('partner_profiles')?.update({ onboarding_step: 4 })?.eq('id', partnerProfile?.id);
    setCurrentStep(4);
  };

  const handleSaveLater = async () => {
    setSaving(true);
    try {
      await supabase
        ?.from('partner_profiles')
        ?.update({ onboarding_step: currentStep })
        ?.eq('id', partnerProfile?.id);
      navigate('/retail-partner-portal', { replace: true });
    } catch (e) {
      navigate('/retail-partner-portal', { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const handleLaunch = () => {
    navigate('/retail-partner-portal', { replace: true });
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(s => s - 1);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0D1B2A' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#00E5FF', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading your onboarding...</p>
        </div>
      </div>
    );
  }

  if (!partnerUser || !partnerProfile) return null;

  const progressPercent = (currentStep / 4) * 100;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0D1B2A', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(0,229,255,0.05), transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(123,47,190,0.08), transparent 70%)' }} />
      </div>

      {/* Top progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-1">
        <div
          className="h-full transition-all duration-700"
          style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #00E5FF, #7B2FBE)' }}
        />
      </div>

      {/* Header */}
      <header
        className="relative z-40 flex items-center justify-between px-8 py-4"
        style={{ borderBottom: '1px solid rgba(0,229,255,0.08)', background: 'rgba(13,27,42,0.95)', backdropFilter: 'blur(20px)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.15), rgba(123,47,190,0.15))', border: '1px solid rgba(0,229,255,0.25)' }}
          >
            <Zap size={18} style={{ color: '#00E5FF' }} />
          </div>
          <div>
            <span className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>FinVisAR</span>
            <span className="ml-2 text-xs font-medium" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>Partner Setup</span>
          </div>
        </div>
        <button
          onClick={handleSaveLater}
          disabled={saving}
          className="flex items-center gap-1.5 text-xs font-medium px-4 py-2 rounded-xl transition-all"
          style={{
            color: '#64748B',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            fontFamily: 'DM Sans, sans-serif',
          }}
        >
          <Save size={13} />
          {saving ? 'Saving...' : 'Save & Continue Later'}
        </button>
      </header>

      {/* Main content */}
      <div className="flex flex-1 relative z-10" style={{ minHeight: 'calc(100vh - 57px - 72px)' }}>
        {/* Step content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-hidden" style={{ transition: 'opacity 0.3s ease' }}>
            {currentStep === 1 && (
              <Step1Welcome
                partnerProfile={partnerProfile}
                partnerUser={partnerUser}
                onComplete={() => handleStepComplete(1)}
              />
            )}
            {currentStep === 2 && (
              <Step2CompanyProfile
                partnerProfile={partnerProfile}
                partnerUser={partnerUser}
                onComplete={() => {
                  handleStepComplete(2);
                  // Refresh profile after save
                  supabase?.from('partner_profiles')?.select('*')?.eq('id', partnerProfile?.id)?.maybeSingle()
                    ?.then(({ data }) => { if (data) setPartnerProfile(data); });
                }}
              />
            )}
            {currentStep === 3 && (
              <Step3FirstProduct
                partnerProfile={partnerProfile}
                onComplete={() => handleStepComplete(3)}
                onSkip={handleProductSkip}
              />
            )}
            {currentStep === 4 && (
              <Step4Checklist
                partnerProfile={partnerProfile}
                partnerUser={partnerUser}
                stepsDone={completedSteps}
                onLaunch={handleLaunch}
              />
            )}
          </div>
        </div>

        {/* Right sidebar */}
        <WizardProgressSidebar currentStep={currentStep} completedSteps={completedSteps} />
      </div>

      {/* Bottom navigation */}
      {currentStep < 4 && (
        <div
          className="relative z-40 flex items-center justify-between px-8 py-4"
          style={{ borderTop: '1px solid rgba(0,229,255,0.08)', background: 'rgba(13,27,42,0.95)', backdropFilter: 'blur(20px)' }}
        >
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all"
            style={{
              background: currentStep === 1 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: currentStep === 1 ? '#334155' : '#94A3B8',
              cursor: currentStep === 1 ? 'not-allowed' : 'pointer',
              fontFamily: 'DM Sans, sans-serif',
            }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          <div className="flex items-center gap-2">
            {[1, 2, 3, 4]?.map(i => (
              <div
                key={i}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === currentStep ? '20px' : '6px',
                  height: '6px',
                  background: completedSteps?.includes(i)
                    ? '#00E5FF'
                    : i === currentStep
                    ? 'linear-gradient(90deg, #00E5FF, #7B2FBE)'
                    : 'rgba(255,255,255,0.1)',
                }}
              />
            ))}
          </div>

          <p className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>Step {currentStep} of 4</p>
        </div>
      )}
    </div>
  );
}
